import { useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { XIcon } from "lucide-react";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

export function Drawer({ open, onClose, title, children }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleEscape);
      requestAnimationFrame(() => {
        panelRef.current?.focus();
      });
      return () => {
        document.body.style.overflow = prev;
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [open, handleEscape]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? "Detail panel"}
    >
      <div
        className="absolute inset-0 bg-black/60 animate-[fadeIn_150ms_ease-out]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative w-full max-w-lg overflow-y-auto bg-surface border-l border-line-subtle shadow-2xl animate-[slideIn_200ms_ease-out] focus:outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line-subtle bg-surface px-5 py-3">
          {title && (
            <h2 className="text-sm font-semibold text-fg truncate pr-4">
              {title}
            </h2>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="ml-auto rounded p-1.5 text-fg-muted hover:text-fg hover:bg-surface-3 focus:outline-none focus:ring-1 focus:ring-electric-blue transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
